import React, { useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { ActivityAgeGenderAxiosInstance } from "../../../Network/ActivityAgeGenderAxiosInstance";

interface FileUploadProps {
  fetchItems?: () => Promise<void>;
}

const FileUpload: React.FC<FileUploadProps> = ({ fetchItems }) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const clearFile = () => {
    const input = document.getElementById(
      "file-upload-age-gender"
    ) as HTMLInputElement | null;

    if (input) {
      input.value = "";
    }
  };

  const formatErrors = (errors: unknown): string => {
    if (!Array.isArray(errors)) {
      return "";
    }

    return errors
      .map((err: any) => {
        if (typeof err === "string") {
          return err;
        }

        return (
          err?.error ||
          err?.message ||
          err?.description ||
          JSON.stringify(err)
        );
      })
      .filter(Boolean)
      .join("\n");
  };

  const getErrorMessage = (error: any): string => {
    const backendData = error?.response?.data;

    const validationErrors = formatErrors(backendData?.errors);

    if (validationErrors) {
      return validationErrors;
    }

    return (
      backendData?.error ||
      backendData?.message ||
      error?.message ||
      "Failed to upload file."
    );
  };

  const refreshItems = async () => {
    if (!fetchItems) {
      return;
    }

    try {
      await fetchItems();
    } catch (error) {
      console.error(
        "File uploaded, but failed to refresh Activity Age Gender list:",
        error
      );

      toast({
        title: "Refresh Error",
        description:
          "The file was uploaded, but the table could not be refreshed. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  const handleUpload = async (selectedFile: File) => {
    const formData = new FormData();

    // The backend upload field is expected to be named "file".
    formData.append("file", selectedFile);

    try {
      setIsUploading(true);

      // ActivityAgeGenderAxiosInstance already uses the ActivityAgeGender base route,
      // matching: POST /ActivityAgeGender/uploadFileActivityAgeGender
      //
      // Do NOT manually set Content-Type here.
      // Axios/browser will set multipart/form-data with the correct boundary.
      const { data } = await ActivityAgeGenderAxiosInstance.post(
        "uploadFileActivityAgeGender",
        formData
      );

      const backendErrors = formatErrors(data?.errors);

      if (data?.success) {
        toast({
          title: backendErrors ? "Upload Completed" : "Success",
          description:
            data?.message ||
            (backendErrors
              ? "File uploaded with validation errors."
              : "File uploaded successfully."),
        });

        await refreshItems();

        if (backendErrors) {
          toast({
            title: "Errors Detected",
            description: backendErrors,
            variant: "destructive",
          });
        }

        return;
      }

      toast({
        title: "Upload Failed",
        description:
          backendErrors ||
          data?.error ||
          data?.message ||
          "Failed to upload file.",
        variant: "destructive",
      });
    } catch (error: any) {
      console.error("Activity Age Gender upload error:", error);

      toast({
        title: "Upload Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);

      // Clear the input even after failure so the same file can be selected again.
      clearFile();
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile || isUploading) {
      return;
    }

    void handleUpload(selectedFile);
  };

  return (
    <div className="inline-flex items-center justify-center">
      <input
        id="file-upload-age-gender"
        type="file"
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      <label
        htmlFor="file-upload-age-gender"
        aria-disabled={isUploading}
        className={`w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700
          hover:from-blue-700 hover:to-blue-800
          flex items-center justify-center rounded-full
          shadow-md transition-all duration-200
          active:scale-95 border border-blue-400/30
          ${
            isUploading
              ? "opacity-50 cursor-not-allowed pointer-events-none"
              : "cursor-pointer"
          }`}
        title={isUploading ? "Uploading..." : "Upload File"}
      >
        <i
          className={
            isUploading
              ? "fa-solid fa-spinner fa-spin text-white text-xl"
              : "fa-solid fa-upload text-white text-xl"
          }
        />
      </label>
    </div>
  );
};

export default FileUpload;
